<template>
    <v-row>
        <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
            <v-card v-if="dialog">
                <v-toolbar dark color="primary">
                    <v-btn icon dark @click="dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title v-if="mode === 'one' && userRow">
                        Change Log &mdash; {{ userRow.name }} | {{ userRow.email }}
                    </v-toolbar-title>
                    <v-toolbar-title v-else>Change Log &mdash; All Users</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                    <v-data-table
                        :loading="loading"
                        :headers="tableHeaders"
                        :items="entries"
                        :server-items-length="totalCount"
                        :options.sync="options"
                        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                        disable-sort
                        show-expand
                        single-expand
                        :expanded.sync="expanded"
                        item-key="id"
                        mobile-breakpoint="100"
                        class="elevation-1 datat mt-4"
                    >
                        <template v-slot:[`item.changed_at`]="{ item }">
                            {{ new Date(item.changed_at).toLocaleDateString('en-IN') }}
                            {{ new Date(item.changed_at).toLocaleTimeString('en-IN') }}
                        </template>
                        <template v-slot:[`item.name`]="{ item }">
                            {{ item.name }}
                            <div class="grey--text text-caption">{{ item.email }}</div>
                        </template>
                        <template v-slot:[`item.operation`]="{ item }">
                            <v-chip :color="opColor(item)" text-color="white" small>{{ opLabel(item) }}</v-chip>
                        </template>
                        <template v-slot:[`item.changed_by_email`]="{ item }">
                            {{ item.changed_by_email || 'System / direct SQL' }}
                        </template>
                        <template v-slot:[`item.changes`]="{ item }">
                            <div v-if="item.operation == 'UPDATE'" class="text-left py-1">
                                <div v-for="diff in diffList(item).slice(0, 4)" :key="diff.label">
                                    <strong>{{ diff.label }}</strong>: {{ diff.oldVal }} &rarr; {{ diff.newVal }}
                                </div>
                                <div v-if="diffList(item).length > 4" class="grey--text">
                                    +{{ diffList(item).length - 4 }} more
                                </div>
                            </div>
                            <div v-else-if="item.operation == 'INSERT'">User created</div>
                            <div v-else>User deleted</div>
                        </template>
                        <template v-slot:expanded-item="{ headers, item }">
                            <td :colspan="headers.length" class="py-3 text-left">
                                <div v-if="item.operation == 'UPDATE'">
                                    <div v-for="diff in diffList(item)" :key="diff.label">
                                        <strong>{{ diff.label }}</strong>: {{ diff.oldVal }} &rarr; {{ diff.newVal }}
                                    </div>
                                    <v-expansion-panels v-if="legacyKyc(item)" flat class="mt-3">
                                        <v-expansion-panel>
                                            <v-expansion-panel-header>Previous eKYC data (raw)</v-expansion-panel-header>
                                            <v-expansion-panel-content>
                                                <vue-json-pretty :data="legacyKyc(item)" :show-line="true" />
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </div>
                                <vue-json-pretty v-else :data="item.new_data || item.old_data" :show-line="true" />
                            </td>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { supabase } from '../supabase'
import VueJsonPretty from 'vue-json-pretty'

const USER_LABELS = {
    id: 'ID',
    created_at: 'Joined On',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    uid: 'UID',
    admin: 'Admin',
    approved: 'Approved',
    deleted: 'Deleted',
    status: 'Status',
    package: 'Package',
    starts_on: 'Starts On',
    expires_on: 'Expires On',
    kyc: 'KYC',
    agreement: 'Agreement',
}

const DATE_FIELDS = ['created_at', 'starts_on', 'expires_on']
const DOC_FIELDS = ['kyc', 'agreement']

export default {
    name: 'UserHistory',
    components: { VueJsonPretty },
    data() {
        return {
            dialog: false,
            mode: 'all',
            userRow: null,
            entries: [],
            totalCount: 0,
            loading: false,
            options: { page: 1, itemsPerPage: 25 },
            expanded: [],
        }
    },
    computed: {
        tableHeaders() {
            const headers = [
                { text: 'When', value: 'changed_at', align: 'center' },
                { text: 'User', value: 'name', align: 'center' },
                { text: 'Action', value: 'operation', align: 'center' },
                { text: 'Changed By', value: 'changed_by_email', align: 'center' },
                { text: 'Changes', value: 'changes', align: 'center' },
                { text: '', value: 'data-table-expand' },
            ]
            return this.mode === 'one' ? headers.filter(h => h.value !== 'name') : headers
        },
    },
    watch: {
        options: {
            handler() {
                if (this.dialog) this.fetchEntries()
            },
            deep: true,
        },
    },
    methods: {
        openForUser(item) {
            this.mode = 'one'
            this.userRow = item
            this.openDialog()
        },
        openAll() {
            this.mode = 'all'
            this.userRow = null
            this.openDialog()
        },
        openDialog() {
            this.entries = []
            this.totalCount = 0
            this.expanded = []
            this.dialog = true
            if (this.options.page !== 1) {
                this.options.page = 1 // watcher triggers fetchEntries
            } else {
                this.fetchEntries()
            }
        },
        async fetchEntries() {
            this.loading = true
            const from = (this.options.page - 1) * this.options.itemsPerPage
            let query = supabase
                .from('users_audit')
                .select('*', { count: 'exact' })
                .order('changed_at', { ascending: false })
                .order('id', { ascending: false })
                .range(from, from + this.options.itemsPerPage - 1)
            if (this.mode === 'one') {
                query = query.eq('user_id', this.userRow.id)
            }
            const { data, error, count } = await query
            if (error) {
                this.$store.dispatch('createAlert', { type: 'error', message: error.message })
            } else {
                this.entries = data
                this.totalCount = count || 0
            }
            this.loading = false
        },
        diffList(entry) {
            const keys = entry.changed_fields && entry.changed_fields.length
                ? entry.changed_fields
                : Object.keys(entry.new_data || {}).filter(
                    k => JSON.stringify((entry.old_data || {})[k]) !== JSON.stringify((entry.new_data || {})[k]))
            return keys.map(k => ({
                label: USER_LABELS[k] || k,
                oldVal: this.formatValue(k, entry.old_data ? entry.old_data[k] : null),
                newVal: this.formatValue(k, entry.new_data ? entry.new_data[k] : null),
            }))
        },
        opLabel(item) {
            return { INSERT: 'Added', UPDATE: 'Updated', DELETE: 'Deleted' }[item.operation] || item.operation
        },
        opColor(item) {
            return { INSERT: 'green', UPDATE: 'orange', DELETE: 'red' }[item.operation] || 'grey'
        },
        formatValue(key, val) {
            if (val === null || val === undefined || val === '') return '—'
            if (DATE_FIELDS.includes(key)) return new Date(val).toLocaleDateString('en-IN')
            if (DOC_FIELDS.includes(key)) return this.formatDoc(val)
            if (typeof val === 'object') {
                const str = JSON.stringify(val)
                return str.length > 120 ? str.slice(0, 120) + '…' : str
            }
            const str = String(val)
            return str.length > 120 ? str.slice(0, 120) + '…' : str
        },
        formatDoc(val) {
            if (typeof val !== 'object' || val === null) return String(val)
            // Rows written before 2026-09-08 hold parsed eKYC XML, not a document ref.
            if (!val.path) return 'legacy eKYC data (expand row to view)'
            const name = val.name || String(val.path).split('/').pop()
            const size = typeof val.size === 'number'
                ? ' · ' + Math.max(1, Math.round(val.size / 1024)) + ' KB'
                : ''
            const when = val.uploaded_at ? new Date(val.uploaded_at) : null
            const stamp = when && !isNaN(when.getTime())
                ? ' · ' + when.toLocaleDateString('en-IN')
                : ''
            return name + size + stamp
        },
        legacyKyc(entry) {
            const k = entry.old_data && entry.old_data.kyc
            return k && typeof k === 'object' && !k.path ? k : null
        },
    },
}
</script>
